import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData as previousPlaceholder,
} from '@tanstack/react-query';
import { useLocation } from 'wouter';

import { useApi } from '../contexts/ApiContext';
import { GameStateResponse, useUpdateGameStateResponse } from './useGame';
import useSetUser from './useSetUser';

export default function useParticipation(participationId, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const {
        data: participation = null,
        isFetched = false,
        ...queryResult
    } = useQuery<Participation>({
        queryKey: ['participation', participationId],
        queryFn: ({ queryKey: [, idParam], signal }) =>
            idParam !== null
                ? api.participations.findById(idParam, { signal })
                : Promise.resolve(null),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        ...opts,
    });

    return {
        participation,
        isFetched,
        ...queryResult,
    };
}

export function useEndParticipation(participation: Participation | null = null) {
    const api = useApi();
    const { id: participationId } = participation || {};
    const updateGameState = useUpdateGameStateResponse();
    const [location] = useLocation();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<GameStateResponse>({
        mutationFn: () => api.participations.end(participationId),
        onSuccess: (response) => {
            updateGameState(response, location);
        },
    });

    return {
        endParticipation: mutate,
        endParticipationAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useSelectPrize(participation: Participation | null = null) {
    const api = useApi();
    const { id: participationId } = participation || {};
    const updateGameState = useUpdateGameStateResponse();
    const [location] = useLocation();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<GameStateResponse>({
        mutationFn: (prize) => api.participations.selectPrize(participationId, prize),
        onSuccess: (response) => {
            updateGameState(response, location);
        },
    });

    return {
        selectPrize: mutate,
        selectPrizeAsync: mutateAsync,
        ...mutationResult,
    };
}
